import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { isNil } from "lodash/fp";
import { useId } from "@reach/auto-id";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import { useOnClickOutside } from "../../hooks/use-on-click-outside.es.js";
import "@reach/rect";
import { useScrollIntoView } from "../../hooks/use-scroll-into-view.es.js";
import { Checkbox } from "../checkbox/checkbox.es.js";
import { Input } from "../input/input.es.js";
import { LabelText } from "../label-text/label-text.es.js";
import { Message } from "../message/message.es.js";
import { MultiSelectOption } from "./multi-select-option.es.js";
import { MultiSelectWrapper, MultiSelectInner, MultiSelectMenu, MultiSelectDropdownBox, MultiSelectPanel, MultiSelectPanelBody, MultiSelectVoid, MultiSelectPanelHead, MultiSelectList, MultiSelectPanelFoot } from "./multi-select.styles.es.js";
import { AnimatedPopover } from "../animated-popover/animated-popover.es.js";
import { SelectButton } from "../select-button/select-button.es.js";
import { Stack } from "../stack/stack.es.js";
import { deprecated } from "../../prop-types-validators/deprecated.es.js";
const valueType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.bool
]);
const optionType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: valueType
});
const propTypes = {
  appearance: deprecated(
    PropTypes.oneOf(["bare", "cell"]),
    "To be used within InputGroup only."
  ),
  className: deprecated(
    PropTypes.string,
    "Avoid extending styles with styled-components."
  ),
  defaultExpanded: PropTypes.bool,
  defaultQuery: PropTypes.string,
  disabled: PropTypes.bool,
  itemsLabel: PropTypes.string,
  label: PropTypes.string,
  menuHeight: PropTypes.number,
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  offset: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(optionType).isRequired,
  placeholder: PropTypes.string,
  tone: PropTypes.oneOf(["crtical", "neutral"]),
  values: PropTypes.arrayOf(valueType),
  variant: PropTypes.oneOf(["default", "inline"]),
  wrap: PropTypes.bool
};
const defaultProps = {
  appearance: void 0,
  className: void 0,
  defaultExpanded: false,
  defaultQuery: "",
  disabled: false,
  itemsLabel: "items",
  label: void 0,
  menuHeight: 240,
  message: void 0,
  offset: 4,
  placeholder: void 0,
  tone: "neutral",
  values: [],
  variant: "default",
  wrap: false
};
const MultiSelect = forwardRef(
  ({
    appearance,
    className,
    defaultExpanded,
    defaultQuery,
    disabled,
    itemsLabel,
    label,
    menuHeight,
    message,
    offset,
    onChange,
    options,
    placeholder,
    tone,
    values,
    variant,
    wrap
  }, forwardedRef) => {
    const id = useId();
    const buttonRef = useRef();
    const menuRef = useRef();
    const listRef = useRef();
    const highlightedRef = useRef();
    const checkboxRef = useRef();
    const [isExpanded, setExpanded] = useState(defaultExpanded);
    const [query, setQuery] = useState(defaultQuery);
    const [highlightedIndex, setHighlightedIndex] = useState(void 0);
    const items = useMemo(
      () => options.filter(
        (item) => String(item.label || item.value).toLowerCase().includes(query.toLowerCase())
      ),
      [options, query]
    );
    const anySelected = items.some((it) => values.includes(it.value));
    const allSelected = items.every((it) => values.includes(it.value));
    const selectedStr = options.filter((opt) => values.includes(opt.value)).map((opt) => opt.label).join(", ");
    const handleBlur = useCallback(({ currentTarget, relatedTarget }) => {
      if (!currentTarget.contains(relatedTarget) && !buttonRef.current.contains(relatedTarget)) {
        setExpanded(false);
      }
    }, []);
    const handleSelect = useCallback(
      (val) => {
        if (values.includes(val)) {
          onChange(values.filter((v) => v !== val));
        } else {
          onChange([...values, val]);
        }
      },
      [onChange, values]
    );
    const handleSelectAll = useCallback(() => {
      if (allSelected) {
        onChange(
          options.filter(
            (opt) => !items.some((it) => it.value === opt.value) && values.includes(opt.value)
          ).map((opt) => opt.value)
        );
      } else {
        onChange(
          options.filter(
            (opt) => items.some((it) => it.value === opt.value) || values.includes(opt.value)
          ).map((opt) => opt.value)
        );
      }
    }, [allSelected, items, onChange, options, values]);
    const handleKeyDown = useCallback(
      (event) => {
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            event.stopPropagation();
            setHighlightedIndex(
              (x) => x === void 0 ? 0 : Math.min(x + 1, items.length - 1)
            );
            break;
          case "ArrowUp":
            event.preventDefault();
            event.stopPropagation();
            setHighlightedIndex(
              (x) => x === void 0 ? 0 : Math.max(0, x - 1)
            );
            break;
          case "Enter":
            event.stopPropagation();
            if (!isNil(highlightedIndex)) {
              const val = items[highlightedIndex].value;
              if (values.includes(val)) {
                onChange(values.filter((v) => v !== val));
              } else {
                onChange([...values, val]);
              }
            }
            break;
          case "Escape":
            event.stopPropagation();
            setExpanded(false);
            buttonRef.current.focus();
            break;
        }
      },
      [highlightedIndex, items, onChange, values]
    );
    useEffect(() => {
      setHighlightedIndex(void 0);
    }, [items, isExpanded]);
    useScrollIntoView(listRef, highlightedRef);
    useOnClickOutside(
      [buttonRef, menuRef],
      useCallback(() => {
        setExpanded(false);
      }, [])
    );
    useImperativeHandle(forwardedRef, () => buttonRef.current);
    return /* @__PURE__ */ jsx(
      MultiSelectWrapper,
      {
        "aria-expanded": isExpanded,
        "aria-haspopup": "listbox",
        "aria-owns": `bungle-multi-select-${id}-popover`,
        className,
        children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
          label && /* @__PURE__ */ jsx(
            LabelText,
            {
              as: "label",
              id: `bungle-multi-select-${id}-label`,
              htmlFor: `bungle-multi-select-${id}-button`,
              children: label
            }
          ),
          /* @__PURE__ */ jsxs(MultiSelectInner, { children: [
            /* @__PURE__ */ jsx(
              SelectButton,
              {
                ref: buttonRef,
                appearance: variant === "inline" ? "inline" : appearance,
                "aria-describedby": `bungle-multi-select-${id}__message`,
                "aria-label": "toggle menu",
                count: values.length > 0 ? values.length : void 0,
                "data-testid": "multi-select-btn",
                disabled,
                expanded: isExpanded,
                id: `bungle-multi-select-${id}-button`,
                onExpand: setExpanded,
                placeholder: placeholder || `Select ${itemsLabel}`,
                tone,
                children: values.length > 0 && (values.length < options.length ? selectedStr : `All ${itemsLabel}`)
              }
            ),
            /* @__PURE__ */ jsx(
              AnimatedPopover,
              {
                id: `bungle-multi-select-${id}-popover`,
                justify: "stretch",
                offset,
                open: isExpanded,
                placement: "bottom",
                targetRef: buttonRef,
                children: /* @__PURE__ */ jsx(
                  MultiSelectMenu,
                  {
                    ref: menuRef,
                    "aria-labelledby": `bungle-select-${id}-label`,
                    "data-testid": "multi-select-menu",
                    onBlur: handleBlur,
                    onKeyDown: handleKeyDown,
                    id: `bungle-multi-select-${id}-menu`,
                    role: "listbox",
                    tabIndex: -1,
                    children: /* @__PURE__ */ jsx(MultiSelectDropdownBox, { children: options.length === 0 ? /* @__PURE__ */ jsx(MultiSelectPanel, { children: /* @__PURE__ */ jsx(MultiSelectPanelBody, { children: /* @__PURE__ */ jsxs(MultiSelectVoid, { children: [
                      "There are no ",
                      itemsLabel,
                      " available."
                    ] }) }) }) : /* @__PURE__ */ jsxs(MultiSelectPanel, { menuHeight, children: [
                      /* @__PURE__ */ jsx(MultiSelectPanelHead, { children: /* @__PURE__ */ jsx(
                        Input,
                        {
                          appearance: "bare",
                          "aria-autocomplete": "list",
                          "aria-controls": `bungle-multi-select-${id}-popover`,
                          autoComplete: "off",
                          autoFocus: true,
                          id: `bungle-multi-select-${id}-search`,
                          magnitude: "large",
                          onChange: setQuery,
                          placeholder: `Filter ${itemsLabel}…`,
                          type: "search",
                          value: query
                        }
                      ) }),
                      /* @__PURE__ */ jsx(MultiSelectPanelBody, { children: items.length === 0 ? /* @__PURE__ */ jsxs(MultiSelectVoid, { children: [
                        "There are no matching ",
                        itemsLabel,
                        "."
                      ] }) : /* @__PURE__ */ jsx(MultiSelectList, { children: items.map((item, index) => /* @__PURE__ */ jsx(
                        MultiSelectOption,
                        {
                          ref: items.indexOf(item) === highlightedIndex ? highlightedRef : void 0,
                          highlighted: index === highlightedIndex,
                          onClick: handleSelect,
                          onMouseEnter: () => {
                            setHighlightedIndex(items.indexOf(item));
                          },
                          selected: values.includes(item.value),
                          value: item.value,
                          wrap,
                          children: item.label
                        },
                        item.value
                      )) }) }),
                      /* @__PURE__ */ jsx(MultiSelectPanelFoot, { children: /* @__PURE__ */ jsxs(
                        Checkbox,
                        {
                          ref: checkboxRef,
                          checked: items.length > 0 && allSelected,
                          disabled: items.length === 0,
                          indeterminate: !allSelected && anySelected,
                          noWrap: true,
                          onChange: handleSelectAll,
                          children: [
                            items.length,
                            " ",
                            itemsLabel
                          ]
                        }
                      ) })
                    ] }) })
                  }
                )
              }
            )
          ] }),
          message && /* @__PURE__ */ jsx(Message, { id: `bungle-multi-select-${id}__message`, tone, children: message })
        ] })
      }
    );
  }
);
MultiSelect.propTypes = propTypes;
MultiSelect.defaultProps = defaultProps;
export {
  MultiSelect
};
